import React, { useEffect, useState } from 'react';
import { C } from './CamerasStyles';
import { pressImHere } from '../../features/security/nightWatch';
import { doorsLine, holdBanners, sirenSounding } from '../../features/security/doors';

// The door alarm on the Cameras pages (GET /security/status `doors`, jobq-api "door sensors"): the doors
// line beside the night-watch pill, the hold / siren banners, and the door chips on a camera's caption.
// Desktop (pages/CamerasPage.jsx, LiveView's CameraTile) and phone (mobile/screens/CamerasScreen.jsx) both
// draw these; the words are features/security/doors.js. Nothing here when `doors` is null.

const plainLink = (href, children, style) => <a href={href} style={style}>{children}</a>;

/** "6 doors closed · panel online" (muted) or the red open / offline line. `renderLink` (desktop) makes it
 *  a link to /doors; without it the line is plain text. */
export function DoorsLine({ doors, renderLink = null }) {
  const line = doorsLine(doors);
  if (!line) return null;
  const color = line.tone === 'bad' ? C.bad : C.muted;
  const body = (
    <>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" style={{ flex: 'none' }}>
        <path d="M5 21V4a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v17M3 21h18M14 12h.01" />
      </svg>
      <span>{line.text}</span>
    </>
  );
  const style = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: line.tone === 'bad' ? 600 : 400, color, textDecoration: 'none' };
  return (
    <span data-testid="doors-line" style={{ display: 'inline-flex' }}>
      {renderLink ? renderLink('/doors', body, style) : <span style={style}>{body}</span>}
    </span>
  );
}

/** The DOOR badge on a door alert row (desktop Alerts list, phone recent list). */
export function DoorBadge() {
  return (
    <span data-testid="door-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 4, background: '#262b31', color: '#c3cad2' }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
        <path d="M5 21V4a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v17M3 21h18M14 12h.01" />
      </svg>
      DOOR
    </span>
  );
}

/** A tile's door chips: the doors whose photo uses this camera, red when open or holding an alert. */
export function DoorChips({ chips }) {
  if (!chips || !chips.length) return null;
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }} data-testid="door-chips">
      {chips.map((c) => {
        const red = c.open || c.hold;
        return (
          <span key={c.zone_id} data-testid="door-chip" data-alarm={red ? 'true' : 'false'}
                title={c.open ? `${c.name} is open` : c.hold ? `${c.name}: an alert is waiting for a check-in` : `${c.name} is closed`}
                style={{ fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 999,
                         background: red ? '#5a1d1d' : '#262b31', color: red ? '#ffd0d0' : '#c3cad2' }}>
            {c.name}{c.open ? ' OPEN' : ''}
          </span>
        );
      })}
    </span>
  );
}

/**
 * The hold banners ("NW Entry Door opened 9:14 PM. Alert in 1:32 unless someone checks in", counting down)
 * and the siren banner, each with the night watch's own I'm here (the typed Admin PIN, pressImHere) and a
 * link to the crew's check-in screen. `ui` = desktopUi / phoneUi.
 */
export function DoorAlarmBanners({ doors, ui, onChanged, renderLink = plainLink, padding = '10px 24px' }) {
  const [, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const banners = holdBanners(doors);
  const siren = sirenSounding(doors);
  const live = banners.length > 0 || siren;

  // The countdown runs by the second while something is pending.
  useEffect(() => {
    if (!live) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [live]);

  if (!live) return null;

  const imHere = async () => {
    setBusy(true);
    setNote('');
    try {
      const r = await pressImHere({ ui, event: 'arrive' });
      if (r && r.ok) {
        const b = r.body || {};
        setNote(`${b.person || 'You'} count${b.person ? 's' : ''} as here now.`);
        if (onChanged) onChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  const buttons = (
    <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
      <button type="button" className="jqc-btn" disabled={busy} onClick={imHere}
              style={{ background: '#fff', color: '#3a1717', borderColor: '#fff', fontWeight: 700 }}>
        I'm here
      </button>
      {renderLink('/check-in', 'Crew without an Admin PIN: check in here', { color: '#ff9c9c', fontSize: 13 })}
    </span>
  );

  return (
    <div style={{ display: 'grid', gap: 8, padding }} data-testid="door-alarm-banners">
      {siren && (
        <div role="alert" data-testid="siren-banner"
             style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10,
                      background: '#7a1616', color: '#fff', fontWeight: 700 }}>
          <span style={{ flex: 1, minWidth: 200 }}>Siren sounding. Check in to stop it.</span>
        </div>
      )}
      {banners.map((b) => (
        <div key={b.event_id} role="alert" data-testid="door-hold-banner"
             style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10,
                      background: '#5a1d1d', color: '#ffe3e3', fontWeight: 600 }}>
          <span style={{ flex: 1, minWidth: 200 }}>{b.text}</span>
        </div>
      ))}
      {buttons}
      {note && <span role="status" style={{ fontSize: 13, color: C.live }}>{note}</span>}
    </div>
  );
}
